import React, { useMemo } from "react";
import { Box, Typography, Card, CardContent, Grid } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  Tooltip,
} from "recharts";

const Demographics = ({ userData, selectedStartDate, selectedEndDate }) => {
  // Process real userData to generate age distribution
  const ageDistributionData = useMemo(() => {
    if (!userData || userData.length === 0) return [];

    // Filter data by date range if provided
    let filteredData = userData;
    if (selectedStartDate && selectedEndDate) {
      const startOfStartDate = new Date(selectedStartDate);
      startOfStartDate.setHours(0, 0, 0, 0);
      const endOfEndDate = new Date(selectedEndDate);
      endOfEndDate.setHours(23, 59, 59, 999);
      
      filteredData = userData.filter((user) => {
        const userDate = new Date(user.saveDate * 1000);
        const userDateTime = userDate.getTime();
        const startDateTime = startOfStartDate.getTime();
        const endDateTime = endOfEndDate.getTime();
        
        return userDateTime >= startDateTime && userDateTime <= endDateTime;
      });
    }

    // Group users by age ranges
    const ageGroups = { '13-17': 0, '18-25': 0, '26-35': 0, '36-45': 0, '46+': 0, 'Unknown': 0 };
    
    filteredData.forEach((user) => {
      if (!user.age) {
        ageGroups['Unknown']++;
      } else if (user.age <= 17) {
        ageGroups['13-17']++;
      } else if (user.age <= 25) {
        ageGroups['18-25']++;
      } else if (user.age <= 35) {
        ageGroups['26-35']++;
      } else if (user.age <= 45) {
        ageGroups['36-45']++;
      } else {
        ageGroups['46+']++;
      }
    });

    return Object.entries(ageGroups).map(([range, count]) => ({ 
      range, 
      count 
    }));
  }, [userData, selectedStartDate, selectedEndDate]);

  // Process data for pie chart with percentages
  const ageDistributionPieData = useMemo(() => {
    const totalUsers = ageDistributionData.reduce((sum, group) => sum + group.count, 0);
    
    const colors = ['#24b8e7', '#4285F4', '#00c853', '#9c27b0', '#F4B400', '#ff9800'];
    
    return ageDistributionData.map((group, index) => ({
      ...group,
      percentage: totalUsers > 0 ? Math.round((group.count / totalUsers) * 100) : 0,
      color: colors[index % colors.length]
    })).filter(item => item.count > 0);
  }, [ageDistributionData]);

  // Custom tooltip for bar chart
  const renderBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = ageDistributionData.reduce((sum, group) => sum + group.count, 0);
      const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
      
      return (
        <div
          style={{
            backgroundColor: "#1a1b23",
            border: "1px solid #333",
            borderRadius: "8px",
            padding: "10px",
            color: "white",
          }}
        >
          <p style={{ margin: 0, color: "#24b8e7", fontWeight: "bold" }}>
            {label}
          </p>
          <p style={{ margin: "4px 0 0 0" }}>
            Count: {data.value} user{data.value !== 1 ? 's' : ''}
          </p>
          <p style={{ margin: "4px 0 0 0" }}>
            Percentage: {percentage}%
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for pie chart
  const renderPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "#1a1b23",
            border: "1px solid #333",
            borderRadius: "8px",
            padding: "10px",
            color: "white",
          }}
        >
          <p style={{ margin: 0, color: "#24b8e7", fontWeight: "bold" }}>
            {data.range}
          </p>
          <p style={{ margin: "4px 0 0 0" }}>
            Count: {data.count} user{data.count !== 1 ? 's' : ''}
          </p>
          <p style={{ margin: "4px 0 0 0" }}>
            Percentage: {data.percentage}%
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom label for pie chart
  const renderCustomizedLabel = (entry) => {
    if (entry.percentage >= 10) {
      return `${entry.range} ${entry.percentage}%`;
    }
    return `${entry.percentage}%`;
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Age Distribution Bar Chart */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              backgroundColor: "#1a1b23",
              border: "1px solid #333",
              height: "400px",
            }}
          >
            <CardContent sx={{ height: "100%", position: "relative" }}>
              <Typography variant="h6" sx={{ color: "white", mb: 1, fontSize: "20px", fontWeight: "600" }}>
                Age Distribution
              </Typography>
              <Typography variant="body2" sx={{ color: "#9e9e9e", mb: 3, fontSize: "14px" }}>
                User demographics by age group
              </Typography>
              
              {/* Horizontal Bar Chart using Recharts */}
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={ageDistributionData}
                  layout="horizontal"
                  margin={{ top: 20, right: 30, left: 60, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.3} />
                  <XAxis 
                    type="number"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9e9e9e", fontSize: 12 }}
                  />
                  <YAxis 
                    dataKey="range"
                    type="category"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9e9e9e", fontSize: 12 }}
                    width={60}
                  />
                  <Tooltip content={renderBarTooltip} />
                  <Bar 
                    dataKey="count" 
                    fill="#00c853"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
              
              {/* Show message if no data */}
              {ageDistributionData.length === 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    color: "#9e9e9e",
                  }}
                >
                  <Typography variant="body2">
                    No age distribution data available for the selected period
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Age Distribution Overview Pie Chart */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              backgroundColor: "#1a1b23",
              border: "1px solid #333",
              height: "400px",
            }}
          >
            <CardContent sx={{ height: "100%", position: "relative" }}>
              <Typography variant="h6" sx={{ color: "white", mb: 1, fontSize: "20px", fontWeight: "600" }}>
                Age Distribution Overview
              </Typography>
              <Typography variant="body2" sx={{ color: "#9e9e9e", mb: 3, fontSize: "14px" }}>
                Visual breakdown of user age groups
              </Typography>
              
              {/* Pie Chart using Recharts */}
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={ageDistributionPieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="count"
                    label={renderCustomizedLabel}
                    labelLine={false}
                    stroke="#1a1b23"
                    strokeWidth={2}
                  >
                    {ageDistributionPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={renderPieTooltip} />
                </PieChart>
              </ResponsiveContainer>
              
              {/* Show message if no data */}
              {ageDistributionPieData.length === 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    color: "#9e9e9e",
                  }}
                >
                  <Typography variant="body2">
                    No age distribution data available for the selected period
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Demographics;